import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCatalog } from "../../Components/CatalogContext";
import { useCart } from "../../Components/CartContext";
import {
  Search,
  ShoppingCart,
  X,
  Menu,
  ChevronDown,
  ListOrdered
} from "lucide-react";
import "./Navbar.css";

const Navbar = () => {
  const [searchVisible, setSearchVisible] = useState(false);
  const [isOverHeader, setIsOverHeader] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const { categories } = useCatalog();
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productDropdownVisible, setProductDropdownVisible] = useState(false);
  const [mobileProductDropdownVisible, setMobileProductDropdownVisible] = useState(false);
  const [eventsDropdownVisible, setEventsDropdownVisible] = useState(false);
  const [mobileEventsDropdownVisible, setMobileEventsDropdownVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartItemsCount } = useCart();
  const cartCount = getCartItemsCount();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (searchInput.trim() === "") {
      setFilteredCategories([]);
      setDropdownVisible(false);
    } else {
      const filtered = categories.filter(category =>
        category.Name.toLowerCase().startsWith(searchInput.toLowerCase())
      );
      setFilteredCategories(filtered);
      setDropdownVisible(filtered.length > 0);
    }
  }, [searchInput, categories]);

  const handleSearchIcon = () => {
    setSearchVisible(!searchVisible);
    if (!searchVisible) {
      setSearchInput("");
      setDropdownVisible(false);
    }
    setMobileMenuOpen(false);
  };

  const handleSearchInputChange = (e) => setSearchInput(e.target.value);

  const handleCategoryClick = (categoryName) => {
    navigate(`/category/${categoryName.toLowerCase()}`);
    setSearchVisible(false);
    setSearchInput("");
    setDropdownVisible(false);
    setProductDropdownVisible(false);
    setMobileMenuOpen(false);
    setMobileProductDropdownVisible(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (searchVisible) setSearchVisible(false);
    if (mobileMenuOpen) setMobileProductDropdownVisible(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setMobileProductDropdownVisible(false);
  };

  const handleProductHover = (isHovering) => setProductDropdownVisible(isHovering);

  const handleMobileProductToggle = () => setMobileProductDropdownVisible(!mobileProductDropdownVisible);

  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      setIsOverHeader(scrollPosition < headerHeight);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (searchVisible || mobileMenuOpen) {
      document.body.classList.add("dimmed");
    } else {
      document.body.classList.remove("dimmed");
    }
  }, [searchVisible, mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuOpen]);

  const hasSolidBackground = searchVisible || !isOverHeader || !isHomePage;
  const iconColor = hasSolidBackground ? "#302620" : "#FFFEF2";

  return (
    <div className="navbar">
      <div
        className={`navComponents ${searchVisible
          ? "solid"
          : isOverHeader && isHomePage
            ? "transparent"
            : "solid"
          }`}
      >
        <div className="navLeft" onClick={() => navigate("/")}>
          <img
            src={`/ok.png`}
            className="navTitle"
            alt="Logo"
          />
        </div>
        {/* Desktop Menu */}
        <div className="navDesktop">
          <div className="navItems">
            <p className="navItem" onClick={() => navigate("/")}>HOME</p>
            <div
              className="navItemWrapper"
              onMouseEnter={() => handleProductHover(true)}
              onMouseLeave={() => handleProductHover(false)}
            >
              <div className="navItemContent">
                <p className="navItem" onClick={() => navigate("/allproduct")}>
                  PRODUCTS
                </p>
                <ChevronDown
                  size={16}
                  color={iconColor}
                  className={`chevronIcon ${productDropdownVisible ? "rotated" : ""}`}
                />
              </div>
              <div className={`productDropdown ${productDropdownVisible ? "visible" : ""}`}>
                <div className="dropdownGrid">
                  {categories.map(category => (
                    <div
                      key={category._id}
                      className="categoryItem"
                      onClick={() => handleCategoryClick(category.Name)}
                    >
                      {category.Name}
                    </div>
                  ))}
                  <div
                    className="categoryItem categoryItem--personalised"
                    onClick={() => { navigate("/customize"); setProductDropdownVisible(false); }}
                  >
                    ✦ Personalised Products
                  </div>
                </div>
              </div>
            </div>
            <p className="navItem" onClick={() => navigate("/about")}>OUR STORY</p>
            <div
              className="navItemWrapper"
              onMouseEnter={() => setEventsDropdownVisible(true)}
              onMouseLeave={() => setEventsDropdownVisible(false)}
            >
              <div className="navItemContent">
                <p className="navItem" onClick={() => navigate("/events")}>EVENTS & CLUBS</p>
                <ChevronDown
                  size={16}
                  color={iconColor}
                  className={`chevronIcon ${eventsDropdownVisible ? "rotated" : ""}`}
                />
              </div>
              <div className={`productDropdown eventsDropdown ${eventsDropdownVisible ? "visible" : ""}`}>
                <div className="dropdownGrid">
                  <div className="categoryItem" onClick={() => { navigate("/events"); setEventsDropdownVisible(false); }}>
                    Events & Clubs
                  </div>
                  <div className="categoryItem" onClick={() => { navigate("/corporate-events"); setEventsDropdownVisible(false); }}>
                  Corporate Events
                  </div>
                  <div className="categoryItem" onClick={() => { navigate("/blog"); setEventsDropdownVisible(false); }}>
                  Blog
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="navIcons">
            <div className="cartIconWrapper" onClick={() => navigate("/cart")}>
              <ShoppingCart size={28} color={iconColor} className="navIcon" />
              {cartCount > 0 && (
                <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>
              )}
            </div>
            <Search size={28} color={iconColor} className="navIcon" onClick={handleSearchIcon} />
          </div>
        </div>
        {/* Mobile Menu Trigger */}
        <div className="navMobile">
          <div className="mobileIcons">
            <Search
              size={24}
              color={iconColor}
              className="navIcon"
              onClick={handleSearchIcon}
            />
            <div className="cartIconWrapper" onClick={() => navigate("/cart")} style={{ marginLeft: "12px" }}>
              <ShoppingCart size={24} color={iconColor} className="navIcon" />
              {cartCount > 0 && (
                <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>
              )}
            </div>
            {mobileMenuOpen ? (
              <X
                size={28}
                color={iconColor}
                className="navIcon hamburger"
                onClick={handleMobileMenuToggle}
                style={{ marginLeft: "12px" }}
              />
            ) : (
              <Menu
                size={28}
                color={iconColor}
                className="navIcon hamburger"
                onClick={handleMobileMenuToggle}
                style={{ marginLeft: "12px" }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobileMenu ${mobileMenuOpen ? "open" : ""}`}>
        <div className="mobileMenuContent">
          <div className="mobileNavItems">
            <p className="mobileNavItem" onClick={() => handleMobileNavigation("/")}>HOME</p>
            <div className="mobileNavItemWrapper">
              <div className="mobileProductHeader" onClick={handleMobileProductToggle}>
                <p className="mobileNavItem">PRODUCTS</p>
                <ChevronDown
                  size={16}
                  color="#302620"
                  className={`mobileChevronIcon ${mobileProductDropdownVisible ? "rotated" : ""}`}
                />
              </div>
              <div className={`mobileCategories ${mobileProductDropdownVisible ? "visible" : "hidden"}`}>
                <div className="allProductsLink" onClick={() => handleMobileNavigation("/allproduct")}>
                  All Products
                </div>
                {categories.map(category => (
                  <div
                    key={category._id}
                    className="mobileCategoryItem"
                    onClick={() => handleCategoryClick(category.Name)}
                  >
                    {category.Name}
                  </div>
                ))}
                <div className="mobileCategoryItem mobileCategoryItem--personalised" onClick={() => handleMobileNavigation("/customize")}>
                  ✦ Personalised Products
                </div>
              </div>
            </div>
            <p className="mobileNavItem" onClick={() => handleMobileNavigation("/about")}>OUR STORY</p>
            <div className="mobileNavItemWrapper">
              <div className="mobileProductHeader" onClick={() => setMobileEventsDropdownVisible(!mobileEventsDropdownVisible)}>
                <p className="mobileNavItem">EVENTS & CLUBS</p>
                <ChevronDown
                  size={16}
                  color="#302620"
                  className={`mobileChevronIcon ${mobileEventsDropdownVisible ? "rotated" : ""}`}
                />
              </div>
              <div className={`mobileCategories ${mobileEventsDropdownVisible ? "visible" : "hidden"}`}>
                <div className="mobileCategoryItem" onClick={() => handleMobileNavigation("/events")}>Events & Clubs</div>
                <div className="mobileCategoryItem" onClick={() => handleMobileNavigation("/corporate-events")}>🏢 Corporate Events</div>
                <div className="mobileCategoryItem" onClick={() => handleMobileNavigation("/blog")}>📖 Blog</div>
              </div>
            </div>
            <p className="mobileNavItem" onClick={() => handleMobileNavigation("/contact")}>CONTACT</p>
          </div>
          <div className="mobileMenuFooter">
            <div className="mobileFooterIcons">
              <div className="mobileIconGroup" onClick={() => handleMobileNavigation("/cart")}>
                <div style={{ position: "relative" }}>
                  <ShoppingCart size={24} color="#302620" />
                  {cartCount > 0 && (
                    <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>
                  )}
                </div>
                <span>Cart</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Search Container */}
      <div className={`navSearchContainer ${searchVisible ? "visible" : "hidden"}`}>
        <div className="navSearch">
          <div className="searchInputContainer">
            <input
              type="text"
              placeholder="Explore"
              className="navSearchInput"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {dropdownVisible && (
              <div className="searchDropdown">
                {filteredCategories.map(category => (
                  <div
                    key={category._id}
                    className="dropdownItem"
                    onClick={() => handleCategoryClick(category.Name)}
                  >
                    {category.Name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="navSearchButton">
            <p>Search</p>
            <Search size={20} color="white" onClick={handleSearchIcon} />
          </button>
        </div>
        <div className="navSearchClose">
          <X size={30} color="#302620" onClick={handleSearchIcon} />
        </div>
      </div>
    </div>
  );
};

export default Navbar;
